// WAV (Voice-Only + Mixed) render entry points + shared primitives the
// MP4-video remux pipeline also builds on (see src/lib/videoExport.ts).
//
// This file intentionally hosts the renderers as plain async functions — the
// hook wrapper in src/hooks/useExport.ts binds them to session/mixer state.

import {
  OFFLINE_RENDER_SAMPLE_RATE,
  OFFLINE_RENDER_CHANNELS,
} from '@shared/constants';
import type { BedPlacement } from './bedPlacement';
import { detectSoundTrack, fetchVideo } from './videoSource';
import { applyEnvelope, computeDuckEnvelope } from './duckAutomation';
import { limitPeaks } from './peakLimiter';

export interface VoiceClipInput {
  /** Raw MP3 (or any decodable audio) bytes for the clip. */
  audio: ArrayBuffer;
  /** Timeline position in seconds. */
  offset: number;
  /** Start of the slice within the decoded buffer. Default 0. */
  inPoint?: number;
  /** End of the slice within the decoded buffer. Default = buffer duration. */
  outPoint?: number;
}

export interface ExportInput {
  totalDuration: number;
  voiceClips: VoiceClipInput[];
  /** Optional video source (File, Blob, URL). Only used for Mixed export. */
  videoSrc?: Blob | string | null;
  /** Music/SFX bed bytes per track (max 2 entries, may be empty). Decoded
   *  inline by the renderer; each entry is mixed on its own gain bus,
   *  ducked alongside the video, so bed-only assets, video-only assets,
   *  and 1- or 2-bed-plus-video assets all render through one code path.
   *  Index aligns with `sfxGains[i]`. */
  sfxBuffers?: ArrayBuffer[];
  voiceGain: number;              // 0..1 — mixer effective voice
  videoGain: number;              // 0..1 — mixer effective video (pre-duck)
  /** Per-track mixer effective SFX gain pre-duck (0..1 each). Index
   *  aligns with `sfxBuffers[i]`. */
  sfxGains: number[];
  /** Per-bed placement (aligned to `sfxBuffers[i]`). Absent → whole buffer
   *  from timeline 0 (back-compat). */
  sfxPlacements?: BedPlacement[];
  duckingEnabled: boolean;
  duckingStrength: number;        // 0..100 percent reduction
  /** When provided, the renderers throw `AbortError` at the next natural
   *  await boundary (between clip decodes, before startRendering). Lets the
   *  upload-cancel flow short-circuit a slow render instead of waiting it
   *  out. Optional — omitted by download/preview callers. */
  signal?: AbortSignal;
  /** Mixed render only: receives the decoded, untouched video audio (before
   *  gain + ducking). The upload flow writes it out as
   *  `_VIDEO_ORIGINAL-AUDIO.wav` — handing the buffer over here avoids
   *  fetching + decoding the video a second time (memory on large videos,
   *  audit 2026-07-17). Not called when the video has no decodable audio. */
  onVideoAudioDecoded?(videoAudio: AudioBuffer): void;
  /** Holt ein frisches Media-Session-Cookie (`ensureMediaSession`). Die
   *  Stream-Route antwortet nach 12 h mit 401; mit dieser Funktion wird dann
   *  einmal erneuert und neu geladen (Audit M18). Setzt `exportSnapshot`. */
  refreshMediaSession?: () => Promise<unknown>;
  /** Nicht-fataler Hinweis fürs Talent — bisher: der Video-Ton fehlt im
   *  Premix, obwohl das Video eine Tonspur hat. Der Render läuft weiter.
   *  Upload: wird zum `warning`-Event der Pipeline; Download: Toast. */
  onWarning?(text: string): void;
  /** Nur Cloud-Video (URL): bekommt die geladene Datei als Blob, bevor ihr
   *  Ton dekodiert wird. Der Aufrufer muxt danach aus genau diesem Blob,
   *  statt das Video ein zweites Mal herunterzuladen (Audit M20). Ohne
   *  Callback liest der Render direkt als ArrayBuffer. */
  onVideoSourceLoaded?(video: Blob): void;
}

/** Braucht der Mixed-Render den Ton des Videos überhaupt? Nein, wenn der
 *  Video-Kanal stumm ist (Fader 0 / Mute) und niemand den Original-Ton haben
 *  will — dann wird das Video gar nicht erst geladen (Audit M20: vorher
 *  lag bei jedem Upload das ganze Video als ArrayBuffer im Tab). */
export function needsVideoAudio(
  input: Pick<ExportInput, 'videoSrc' | 'videoGain' | 'onVideoAudioDecoded'>,
): boolean {
  if (!input.videoSrc) return false;
  return input.videoGain > 0 || !!input.onVideoAudioDecoded;
}

/** Texte der Premix-Warnungen — exportiert für Tests und Aufrufer. */
export const VIDEO_AUDIO_LOAD_WARNING =
  "The video's sound could not be loaded — the premix was rendered without it.";
export const VIDEO_AUDIO_DECODE_WARNING =
  "The video's sound could not be decoded — the premix was rendered without it.";

/** Merge overlapping voice intervals so ducking doesn't un-duck mid-overlap. */
export function mergeIntervals(
  intervals: Array<[number, number]>
): Array<[number, number]> {
  if (intervals.length === 0) return [];
  const sorted = intervals.slice().sort((a, b) => a[0] - b[0]);
  const out: Array<[number, number]> = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];
    const last = out[out.length - 1];
    if (s <= last[1]) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  }
  return out;
}

async function decodeAudio(
  ac: OfflineAudioContext | AudioContext,
  bytes: ArrayBuffer
): Promise<AudioBuffer> {
  return ac.decodeAudioData(bytes.slice(0));
}

/** Brick-wall limiter (#173) used on the Mixed master bus. Settings tuned
 *  for spoken-voice content: threshold -1 dBFS, ratio 20:1, hard knee,
 *  1ms attack, 100ms release. Catches inter-voice summing peaks without
 *  audible pumping. Exported so callers (audioExport + videoExport, both
 *  consume `renderMixedAudioBuffer`) can verify the placement is unified.
 */
export function createBrickwallLimiter(
  ctx: BaseAudioContext
): DynamicsCompressorNode {
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -1;   // dBFS
  limiter.ratio.value = 20;
  limiter.attack.value = 0.001;   // 1ms
  limiter.release.value = 0.1;    // 100ms
  limiter.knee.value = 0;         // hard knee
  return limiter;
}

/** Render the Voice-Only timeline at unity gain (mixer is ignored — matches
 *  legacy behavior; the output is intended for NLE muxing). */
export async function renderVoiceOnlyBuffer(
  input: ExportInput
): Promise<AudioBuffer> {
  const ac = new OfflineAudioContext(
    OFFLINE_RENDER_CHANNELS,
    Math.ceil(input.totalDuration * OFFLINE_RENDER_SAMPLE_RATE),
    OFFLINE_RENDER_SAMPLE_RATE
  );
  const gain = ac.createGain();
  gain.gain.value = 1.0;
  gain.connect(ac.destination);

  for (const clip of input.voiceClips) {
    input.signal?.throwIfAborted();
    let buf: AudioBuffer;
    try {
      buf = await decodeAudio(ac, clip.audio);
    } catch (e) {
      console.warn('[export] voice-clip decode failed, skipping', e);
      continue;
    }
    const inP = clip.inPoint ?? 0;
    const outP = clip.outPoint ?? buf.duration;
    const sliceLen = Math.max(0, outP - inP);
    if (sliceLen <= 0) continue;
    const startAt = Math.max(0, clip.offset);
    const remaining = input.totalDuration - startAt;
    if (remaining <= 0) continue;
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.connect(gain);
    src.start(startAt, inP, Math.min(sliceLen, remaining));
  }
  input.signal?.throwIfAborted();
  return ac.startRendering();
}

/** Render Voice + Video (with mixer + ducking) as a final AudioBuffer.
 *  Video audio that can't be decoded (exotic codecs, Firefox) is skipped —
 *  the result then matches `renderVoiceOnlyBuffer` gain-staging (but still
 *  applies the voice-mixer volume, unlike the "voice-only" unity output).
 *
 *  Brick-Wall-Limiter (#173): Mixed-Pfade können mehrere überlappende Voices
 *  summieren (Multi-Voice Dialog), die einzeln pegelmäßig sauber sind aber
 *  gemixt clippen können. DynamicsCompressorNode mit hartem knee, threshold
 *  -1 dBFS, ratio 20:1 fängt das meiste ohne hörbares Pumpen ab; weil seine
 *  1-ms-Attack schnelle Transienten durchlässt, folgt nach dem Render ein
 *  Look-ahead-Limiter auf dem PCM (`limitPeaks`, garantiert ≤ -1 dBFS,
 *  2026-09-24). Voice-Only-Pfad bleibt raw — Producer braucht den
 *  unkomprimierten Stem.
 *
 *  Diese Funktion wird sowohl von `renderMixedWav` (Mixed-WAV-Export) als
 *  auch von `videoExport.ts` (MP4-Audio-Bus) aufgerufen — der Limiter
 *  greift dadurch in beiden Export-Pfaden. */
export async function renderMixedAudioBuffer(
  input: ExportInput
): Promise<AudioBuffer> {
  const ac = new OfflineAudioContext(
    OFFLINE_RENDER_CHANNELS,
    Math.ceil(input.totalDuration * OFFLINE_RENDER_SAMPLE_RATE),
    OFFLINE_RENDER_SAMPLE_RATE
  );

  // Master sum bus → brick-wall limiter → destination. All voice/video/SFX
  // buses route here instead of `ac.destination` so the limiter sees the
  // full summed mix.
  const masterMix = ac.createGain();
  masterMix.gain.value = 1.0;
  const limiter = createBrickwallLimiter(ac);
  masterMix.connect(limiter);
  limiter.connect(ac.destination);

  const voiceGain = ac.createGain();
  voiceGain.gain.value = input.voiceGain;
  voiceGain.connect(masterMix);

  const voiceIntervals: Array<[number, number]> = [];
  for (const clip of input.voiceClips) {
    input.signal?.throwIfAborted();
    let buf: AudioBuffer;
    try {
      buf = await decodeAudio(ac, clip.audio);
    } catch (e) {
      console.warn('[export] voice-clip decode failed, skipping', e);
      continue;
    }
    const inP = clip.inPoint ?? 0;
    const outP = clip.outPoint ?? buf.duration;
    const sliceLen = Math.max(0, outP - inP);
    if (sliceLen <= 0) continue;
    const startAt = Math.max(0, clip.offset);
    const remaining = input.totalDuration - startAt;
    if (remaining <= 0) continue;
    const playLen = Math.min(sliceLen, remaining);
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.connect(voiceGain);
    src.start(startAt, inP, playLen);
    voiceIntervals.push([startAt, startAt + playLen]);
  }

  // Voice-active intervals drive ducking on BOTH the video and the SFX
  // bed. Merge once, share across both buses.
  const merged =
    input.duckingEnabled && voiceIntervals.length > 0
      ? mergeIntervals(voiceIntervals)
      : [];

  /** Render an auxiliary bus (video audio or SFX bed) with the shared
   *  duck automation. Decoded buffer is passed in to keep the two callers
   *  symmetric. */
  const scheduleDuckedBus = (
    audioBuf: AudioBuffer,
    baseVol: number,
    placement?: { timelineStart: number; sourceStart: number; playLen: number },
  ) => {
    // Compute window bounds BEFORE allocating any nodes — if the bed is fully
    // outside the export window (playLen <= 0) we skip entirely, avoiding an
    // orphan GainNode that would never be connected to a source.
    const timelineStart = placement?.timelineStart ?? 0;
    const sourceStart = placement?.sourceStart ?? 0;
    const rawLen = placement?.playLen ?? audioBuf.duration;
    const playLen = Math.min(
      rawLen,
      audioBuf.duration - sourceStart,
      input.totalDuration - timelineStart,
    );
    if (playLen <= 0) return;

    const bus = ac.createGain();
    bus.gain.value = baseVol;
    bus.connect(masterMix);

    if (input.duckingEnabled && merged.length > 0 && baseVol > 0) {
      // Dieselbe Hüllkurve wie die Vorschau: 2 ms Attack / 300 ms Release,
      // jeder Übergang vom aktuellen Pegel aus (Audit M19 — vorher harte
      // setValueAtTime-Sprünge mit Klicks an jeder Blockkante).
      applyEnvelope(
        bus.gain,
        computeDuckEnvelope({
          intervals: merged,
          base: baseVol,
          ducked: baseVol * (1 - input.duckingStrength / 100),
        }),
      );
    }

    const src = ac.createBufferSource();
    src.buffer = audioBuf;
    src.connect(bus);
    src.start(timelineStart, sourceStart, playLen);
  };

  if (needsVideoAudio(input)) {
    input.signal?.throwIfAborted();
    const videoAudio = await loadVideoAudio(ac, input);
    if (videoAudio) {
      input.onVideoAudioDecoded?.(videoAudio);
      scheduleDuckedBus(videoAudio, input.videoGain);
    }
  }

  // Decode + schedule each loaded bed track (max 2). Each gets its own
  // ducked-bus so per-track gains apply individually but the duck
  // automation is shared with voice/video timing.
  const sfxBuffers = input.sfxBuffers ?? [];
  const sfxPlacements = input.sfxPlacements ?? [];
  for (let i = 0; i < sfxBuffers.length; i++) {
    input.signal?.throwIfAborted();
    try {
      const sfxAudio = await decodeAudio(ac, sfxBuffers[i]);
      scheduleDuckedBus(sfxAudio, input.sfxGains[i] ?? 0, sfxPlacements[i]);
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') throw e;
      console.warn(
        `[export] sfx bed ${i + 1} decode failed — continuing without it`,
        e,
      );
    }
  }

  input.signal?.throwIfAborted();
  const rendered = await ac.startRendering();
  // Der Kompressor oben ist kein Brickwall (1 ms Attack): schnelle
  // Transienten rutschen durch und würden in audioBufferToWav / im AAC hart
  // clippen. Look-ahead-Limiter auf dem fertigen PCM — greift nur, wenn
  // wirklich etwas über -1 dBFS liegt (peakLimiter.ts).
  const channels: Float32Array[] = [];
  for (let c = 0; c < rendered.numberOfChannels; c++) channels.push(rendered.getChannelData(c));
  const lim = limitPeaks(channels, rendered.sampleRate);
  if (lim.applied) {
    console.info(
      `[export] safety limiter engaged — input peak ${(20 * Math.log10(lim.inputPeak)).toFixed(2)} dBFS`,
    );
  }
  return rendered;
}

/** Lädt + dekodiert den Ton des Referenzvideos für den Premix. `null`, wenn
 *  es keinen gibt. Fehler bleiben nicht mehr still (Audit M18):
 *
 *  - Laden scheitert (HTTP-Fehler auch nach Session-Refresh, Netz) → Warnung.
 *  - Dekodieren scheitert und die Datei HAT eine Tonspur (oder es ist nicht
 *    erkennbar, z. B. WebM) → Warnung.
 *  - Dekodieren scheitert bei einem Video ohne Tonspur → keine Warnung: Es
 *    fehlt nichts, ein stummes Referenzvideo ist ein normaler Fall.
 *
 *  Ein Abbruch durch das Talent (AbortError) geht immer nach oben durch. */
async function loadVideoAudio(
  ac: OfflineAudioContext,
  input: ExportInput,
): Promise<AudioBuffer | null> {
  const src = input.videoSrc;
  if (!src) return null;
  let bytes: ArrayBuffer;
  try {
    if (typeof src === 'string') {
      const res = await fetchVideo(src, {
        signal: input.signal,
        refreshMediaSession: input.refreshMediaSession,
      });
      if (input.onVideoSourceLoaded) {
        // Als Blob holen (große Blobs lagert der Browser außerhalb des
        // Tab-Heaps aus) und weiterreichen — der Mux liest ihn per stream().
        const blob = await res.blob();
        input.onVideoSourceLoaded(blob);
        bytes = await blob.arrayBuffer();
      } else {
        bytes = await res.arrayBuffer();
      }
    } else {
      bytes = await src.arrayBuffer();
    }
  } catch (e) {
    if ((e as DOMException)?.name === 'AbortError') throw e;
    console.warn('[export] video could not be loaded — premix without video audio', e);
    input.onWarning?.(VIDEO_AUDIO_LOAD_WARNING);
    return null;
  }
  input.signal?.throwIfAborted();
  // Vor dem Dekodieren: decodeAudioData detacht `bytes`, danach ist nichts
  // mehr zu lesen.
  const sound = detectSoundTrack(new Uint8Array(bytes));
  try {
    // decodeAudioData directly — NOT via decodeAudio(): that helper
    // slice(0)-copies so store-owned buffers survive the decoder's
    // detach. `bytes` is local here, and for >1GB reference videos the
    // extra full-file copy doubled peak memory (audit 2026-07-17).
    return await ac.decodeAudioData(bytes);
  } catch (e) {
    if ((e as DOMException)?.name === 'AbortError') throw e;
    if (sound === 'no') {
      console.info('[export] video has no sound track — premix without video audio');
      return null;
    }
    console.warn('[export] video audio decode failed — continuing without it', e);
    input.onWarning?.(VIDEO_AUDIO_DECODE_WARNING);
    return null;
  }
}

// ─── WAV wrappers (thin) ────────────────────────────────────────────────────

export async function renderVoiceOnlyWav(input: ExportInput): Promise<Blob> {
  const buf = await renderVoiceOnlyBuffer(input);
  return audioBufferToWav(buf);
}

export async function renderMixedWav(input: ExportInput): Promise<Blob> {
  const buf = await renderMixedAudioBuffer(input);
  return audioBufferToWav(buf);
}

/** 16-bit PCM RIFF WAV encoder. */
export function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numCh = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const len = buffer.length;
  const bytesPerSample = 2;
  const dataSize = len * numCh * bytesPerSample;
  const out = new ArrayBuffer(44 + dataSize);
  const view = new DataView(out);

  const writeStr = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);      // PCM
  view.setUint16(22, numCh, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numCh * bytesPerSample, true);
  view.setUint16(32, numCh * bytesPerSample, true);
  view.setUint16(34, 8 * bytesPerSample, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));

  let offset = 44;
  for (let i = 0; i < len; i++) {
    for (let c = 0; c < numCh; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([out], { type: 'audio/wav' });
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
